// SPDX-License-Identifier: LicenseRef-Proprietary
/**
 * Authentication Service for Cognito Integration
 * 
 * This service handles authentication flows with AWS Cognito Hosted UI,
 * including login, logout, and token management.
 */

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface UserInfo {
  email: string;
  name?: string;
  sub: string;
}

export class AuthService {
  // NOTE: COGNITO_DOMAIN should be set via REACT_APP_COGNITO_DOMAIN environment variable
  // Do not hardcode production values - use environment variables or build-time configuration
  private static readonly COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || '';
  private static readonly CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '';
  
  // Environment detection
  private static readonly isDev =
    (process.env.REACT_APP_ENVIRONMENT || '').toLowerCase() === 'dev' ||
    process.env.NODE_ENV === 'development';
  
  // Server endpoints for production, client endpoints for development
  private static readonly LOGIN_PATH = process.env.REACT_APP_LOGIN_PATH || (this.isDev ? '/auth/login' : '/api/auth/login');
  private static readonly LOGOUT_PATH = process.env.REACT_APP_LOGOUT_PATH || (this.isDev ? '/auth/logout' : '/api/auth/logout');
  private static readonly CALLBACK_PATH = process.env.REACT_APP_AUTH_CALLBACK_PATH || (this.isDev ? '/auth/callback' : '/api/auth/callback');
  private static readonly REDIRECT_URI = `${window.location.origin}${this.CALLBACK_PATH}`;
  
  // Check if authentication is properly configured - check runtime status from backend
  private static _authEnabled: boolean | null = null;
  private static _runtimeConfig: { cognito_domain?: string; client_id?: string; redirect_uri?: string } | null = null;
  private static _backendUnavailable: boolean = false;
  
  /**
   * Fetch authentication status from backend (includes user info if authenticated)
   */
  static async fetchStatus(): Promise<{ authenticated: boolean; name?: string; email?: string } | null> {
    try {
      const response = await fetch('/api/auth/status', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        return {
          authenticated: data.authenticated === true,
          name: data.name,
          email: data.email
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch auth status:', error);
      return null;
    }
  }

  /**
   * Robust JSON fetch helper that validates content-type
   */
  private static async getJson(url: string) {
    const r = await fetch(url, { credentials: 'include' });
    const ct = r.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      throw new Error(`Expected JSON from ${url}, got ${ct}`);
    }
    return r.json();
  }

  /**
   * Check if authentication is enabled
   */
  static async isAuthEnabled(): Promise<boolean> {
    if (this._authEnabled !== null) {
      return this._authEnabled;
    }
    
    try {
      const data = await this.getJson('/api/auth/status');
      console.log('[auth] Fetched auth status from backend:', data);
      this._authEnabled = Boolean(data?.auth_enabled ?? data?.enabled);
      this._runtimeConfig = {
        cognito_domain: data.cognito_domain,
        client_id: data.client_id,
        redirect_uri: data.redirect_uri
      };
      return this._authEnabled;
    } catch (error) {
      console.error('Failed to check auth status:', error);
      this._backendUnavailable = true;
      // If backend is down, but we have sufficient env to initiate Hosted UI directly,
      // treat auth as enabled so we can fall back to direct Cognito login.
      const hasDirectConfig = (this.CLIENT_ID?.length || 0) > 0 && (this.COGNITO_DOMAIN?.length || 0) > 0;
      if (hasDirectConfig) {
        this._authEnabled = true;
        return true;
      }
      // Dev fallback: infer enablement from env
      if (this.isDev) {
        this._authEnabled = this.CLIENT_ID.length > 0;
        return this._authEnabled;
      }
      // Production and no direct config available
      throw new Error('Authentication temporarily unavailable (EHTA-AUTH-001).');
    }
  }
  
  /**
   * Check if user is currently authenticated
   */
  static async isAuthenticated(): Promise<boolean> {
    const enabled = await this.isAuthEnabled();
    if (!enabled) return true;
    const status = await this.fetchStatus();
    return !!status?.authenticated;
  }
  
  /**
   * Get current access token
   */
  static getAccessToken(): string | null {
    return sessionStorage.getItem('access_token');
  }
  
  /**
   * Get current ID token
   */
  static getIdToken(): string | null {
    return sessionStorage.getItem('id_token');
  }
  
  /**
   * Get current refresh token
   */
  static getRefreshToken(): string | null {
    return sessionStorage.getItem('refresh_token');
  }
  
  /**
   * Get user information from ID token
   */
  static getUserInfo(): UserInfo | null {
    // For prod, components should call fetchStatus() instead (HttpOnly cookie).
    // Keep this for dev only.
    if (!this.isDev) return null;
    const idToken = this.getIdToken();
    if (!idToken) return null;
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return { email: payload.email, name: payload.name, sub: payload.sub };
    } catch { return null; }
  }
  
  /**
   * Get runtime configuration values
   */
  private static getRuntimeConfig() {
    return this._runtimeConfig || {};
  }

  /**
   * Get the Cognito domain (runtime or fallback)
   */
  private static getCognitoDomain(): string {
    const config = this.getRuntimeConfig();
    return config.cognito_domain || this.COGNITO_DOMAIN;
  }

  /**
   * Get the client ID (runtime or fallback)
   */
  private static getClientId(): string {
    const config = this.getRuntimeConfig();
    return config.client_id || this.CLIENT_ID;
  }

  /**
   * Get the redirect URI (runtime or fallback)
   */
  private static getRedirectUri(): string {
    const config = this.getRuntimeConfig();
    return config.redirect_uri || this.REDIRECT_URI;
  }

  /**
   * Initiate login flow with Cognito Hosted UI
   */
  static async initiateLogin(): Promise<void> {
    const authEnabled = await this.isAuthEnabled();
    if (!authEnabled) {
      console.warn('Authentication is not enabled - running in development mode');
      return;
    }
    
    // In production: prefer server endpoint when backend is healthy, otherwise fall back to direct Hosted UI
    if (!this.isDev) {
      if (!this._backendUnavailable) {
        try {
          // Best-effort ping to avoid bouncing the user into a 502
          // If it fails, fall back to direct Cognito login.
          window.location.assign(this.LOGIN_PATH);
          return;
        } catch (_) {
          // ignore and fall through to direct login
        }
      }
      // Backend unavailable or redirect failed: direct Cognito login
      console.info('[auth] Falling back to direct Cognito Hosted UI login (backend unavailable)');
      const cognitoDomain = this.getCognitoDomain();
      const clientId = this.getClientId();
      const redirectUri = this.getRedirectUri();
      if (!clientId) {
        console.error('No client ID available for authentication');
        return;
      }
      const state = this.generateState();
      sessionStorage.setItem('auth_state', state);
      const authUrl = cognitoDomain.includes('.auth.eu-central-1.amazoncognito.com') 
        ? `https://${cognitoDomain}/login?`
        : `https://${cognitoDomain}.auth.eu-central-1.amazoncognito.com/login?`;
      const fullUrl = authUrl +
        `client_id=${clientId}&` +
        `response_type=code&` +
        `scope=email+openid+profile&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `state=${state}`;
      window.location.href = fullUrl;
      return;
    }
    
    // Dev fallback: direct Cognito URL construction
    const cognitoDomain = this.getCognitoDomain();
    const clientId = this.getClientId();
    const redirectUri = this.getRedirectUri();
    
    if (!clientId) {
      console.error('No client ID available for authentication');
      return;
    }
    
    const state = this.generateState();
    sessionStorage.setItem('auth_state', state);
    
    // Check if domain already includes the full URL
    const authUrl = cognitoDomain.includes('.auth.eu-central-1.amazoncognito.com') 
      ? `https://${cognitoDomain}/login?`
      : `https://${cognitoDomain}.auth.eu-central-1.amazoncognito.com/login?`;
    
    const fullUrl = authUrl +
      `client_id=${clientId}&` +
      `response_type=code&` +
      `scope=email+openid+profile&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `state=${state}`;
    
    window.location.href = fullUrl;
  }
  
  /**
   * Initiate logout flow
   */
  static async initiateLogout(): Promise<void> {
    // Clear client caches
    this.clearAuth();
    
    const authEnabled = await this.isAuthEnabled();
    if (!authEnabled) {
      console.warn('Authentication is not enabled - running in development mode');
      return;
    }
    
    // In production, use server-side logout endpoint
    if (!this.isDev) {
      window.location.assign(this.LOGOUT_PATH);
      return;
    }
    
    // Dev fallback: direct Cognito logout URL
    const cognitoDomain = this.getCognitoDomain();
    const clientId = this.getClientId();
    
    // Check if domain already includes the full URL
    const logoutUrl = cognitoDomain.includes('.auth.eu-central-1.amazoncognito.com') 
      ? `https://${cognitoDomain}/logout?`
      : `https://${cognitoDomain}.auth.eu-central-1.amazoncognito.com/logout?`;
    
    const fullLogoutUrl = logoutUrl +
      `client_id=${clientId}&` +
      `logout_uri=${encodeURIComponent(window.location.origin)}`;
    
    window.location.href = fullLogoutUrl;
  }
  
  /**
   * Handle authentication callback
   */
  static async handleCallback(): Promise<boolean> {
    // In prod the server handled /api/auth/callback and set HttpOnly cookies.
    if (!this.isDev) {
      window.location.replace('/');
      return true;
    }
    
    // Dev fallback: keep client-side code exchange if you really need it locally.
    return new Promise((resolve) => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      
      if (error) {
        console.error('Authentication error:', error);
        resolve(false);
        return;
      }
      
      if (!code) {
        console.error('No authorization code received');
        resolve(false);
        return;
      }
      
      // Verify state parameter for CSRF protection
      const storedState = sessionStorage.getItem('auth_state');
      if (state !== storedState) {
        console.error('Invalid state parameter');
        resolve(false);
        return;
      }
      
      // Clear state
      sessionStorage.removeItem('auth_state');
      
      // Exchange authorization code for tokens
      this.exchangeCodeForTokens(code)
        .then((tokens) => {
          const mapped = this.mapTokens(tokens);
          if (mapped) {
            this.storeTokens(mapped);
            resolve(true);
          } else {
            console.error('Failed to exchange code for tokens - no tokens returned');
            resolve(false);
          }
        })
        .catch((error) => {
          console.error('Token exchange error:', error);
          resolve(false);
        });
    });
  }
  
  /**
   * Exchange authorization code for tokens
   */
  private static async exchangeCodeForTokens(code: string): Promise<any> {
    try {
      const cognitoDomain = this.getCognitoDomain();
      const clientId = this.getClientId();
      const redirectUri = this.getRedirectUri();
      
      // Check if domain already includes the full URL
      const tokenUrl = cognitoDomain.includes('.auth.eu-central-1.amazoncognito.com') 
        ? `https://${cognitoDomain}/oauth2/token`
        : `https://${cognitoDomain}.auth.eu-central-1.amazoncognito.com/oauth2/token`;
      
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          code: code,
          redirect_uri: redirectUri,
        }),
      });

      if (!response.ok) {
        console.error('Token exchange failed:', response.status, response.statusText);
        return null;
      }

      const tokens = await response.json();
      return tokens;
    } catch (error) {
      console.error('Token exchange error:', error);
      return null;
    }
  }

  /**
   * Normalize token payloads (snake_case from Cognito → camelCase)
   */
  private static mapTokens(raw: any): AuthTokens | null {
    if (!raw) return null;
    if ('access_token' in raw || 'id_token' in raw) {
      return {
        accessToken: raw.access_token || '',
        idToken: raw.id_token || '',
        refreshToken: raw.refresh_token || '',
        expiresIn: raw.expires_in || 0,
      };
    }
    if ('accessToken' in raw || 'idToken' in raw) {
      return {
        accessToken: raw.accessToken || '',
        idToken: raw.idToken || '',
        refreshToken: raw.refreshToken || '',
        expiresIn: raw.expiresIn || 0,
      };
    }
    return null;
  }

  /**
   * Refresh access token using refresh token
   */
  static async refreshAccessToken(): Promise<boolean> {
    // In production, use server-side refresh endpoint
    if (!this.isDev) {
      const resp = await fetch('/api/auth/refresh', { method: 'GET', credentials: 'include' });
      return resp.status === 204;
    }
    
    // Dev fallback: client-side refresh
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;
    
    try {
      const response = await fetch('/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      
      if (response.ok) {
        const tokens = await response.json();
        const mapped = this.mapTokens(tokens);
        if (mapped) {
          this.storeTokens(mapped);
          return true;
        }
        console.error('Token refresh response missing tokens');
        return false;
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }
  
  /**
   * Store authentication tokens
   */
  static storeTokens(tokens: AuthTokens): void {
    sessionStorage.setItem('access_token', tokens.accessToken);
    sessionStorage.setItem('id_token', tokens.idToken);
    sessionStorage.setItem('refresh_token', tokens.refreshToken);
    sessionStorage.setItem('token_expires_at', (Date.now() + tokens.expiresIn * 1000).toString());
  }
  
  /**
   * Clear all authentication data
   */
  static clearAuth(): void {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('token_expires_at');
    sessionStorage.removeItem('auth_state');
  }
  
  /**
   * Check if token is expired
   */
  private static isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= exp;
    } catch (error) {
      console.error('Failed to check token expiration:', error);
      return true;
    }
  }
  
  /**
   * Generate random state parameter for CSRF protection
   */
  private static generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }
  
  /**
   * Get authorization header for API requests
   */
  static getAuthHeader(): { Authorization: string } | {} {
    // In prod we rely on cookies; keep Bearer only for dev tools hitting local backend.
    if (!this.isDev) return {};
    const accessToken = this.getAccessToken();
    return accessToken && !this.isTokenExpired(accessToken)
      ? { Authorization: `Bearer ${accessToken}` }
      : {};
  }
}
